/**
 * QuantityInput — number input with mobile-friendly +/− stepper buttons.
 *
 * Drop-in replacement for the five custom-order / product-buy modals
 * across PublicStore.jsx and Armstrong.jsx. Fixes the long-standing
 * "can't edit how many bags" bug AND adds the +/− affordance buyers
 * expect on mobile checkout.
 *
 * Behavior:
 *   - Field allows empty while typing (no snap-back to 1 on backspace).
 *   - Coerces to clamped integer on blur and on form submit.
 *   - +/− buttons clamp at min/max and are disabled at the boundary
 *     so users can't tap into nonsense.
 *   - Buttons are 44px tall — Apple's minimum touch target.
 */
import React, { useState, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { MinusIcon as Minus, PlusIcon as Plus } from "@phosphor-icons/react";

export function QuantityInput({
  value,
  onChange,
  min = 1,
  max = 99,
  className = "w-16",
  ariaLabel = "Quantity",
  testid = "quantity-input",
}) {
  const [local, setLocal] = useState(String(value ?? min));

  useEffect(() => {
    setLocal(String(value ?? min));
  }, [value, min]);

  const handleChange = (e) => {
    const raw = e.target.value;
    if (raw === "") {
      setLocal("");
      return;
    }
    if (!/^\d+$/.test(raw)) return;
    setLocal(raw);
    const n = parseInt(raw, 10);
    if (!Number.isNaN(n)) onChange(Math.min(max, Math.max(min, n)));
  };

  const handleBlur = () => {
    if (local === "" || Number.isNaN(parseInt(local, 10))) {
      setLocal(String(min));
      onChange(min);
    } else {
      const clamped = Math.min(max, Math.max(min, parseInt(local, 10)));
      setLocal(String(clamped));
      onChange(clamped);
    }
  };

  const step = (delta) => {
    const current = parseInt(local, 10);
    const base = Number.isNaN(current) ? min : current;
    const next = Math.min(max, Math.max(min, base + delta));
    setLocal(String(next));
    onChange(next);
  };

  const numericValue = parseInt(local, 10);
  const atMin = !Number.isNaN(numericValue) && numericValue <= min;
  const atMax = !Number.isNaN(numericValue) && numericValue >= max;

  return (
    <div
      className="inline-flex items-center rounded-md border border-stone-300 bg-white overflow-hidden"
      data-testid={`${testid}-stepper`}
    >
      <button
        type="button"
        onClick={() => step(-1)}
        disabled={atMin}
        aria-label="Decrease quantity"
        className="h-11 w-11 flex items-center justify-center text-stone-700 hover:bg-stone-50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
        data-testid={`${testid}-decrement`}
      >
        <Minus size={16} weight="bold" />
      </button>
      <Input
        type="number"
        inputMode="numeric"
        min={min}
        max={max}
        value={local}
        onChange={handleChange}
        onBlur={handleBlur}
        className={`h-11 text-center border-0 border-x border-stone-300 rounded-none focus-visible:ring-0 ${className}`}
        aria-label={ariaLabel}
        data-testid={testid}
      />
      <button
        type="button"
        onClick={() => step(1)}
        disabled={atMax}
        aria-label="Increase quantity"
        className="h-11 w-11 flex items-center justify-center text-stone-700 hover:bg-stone-50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
        data-testid={`${testid}-increment`}
      >
        <Plus size={16} weight="bold" />
      </button>
    </div>
  );
}
